import api from 'services/api';
import { enforce, runSchema } from 'validation';

import { schedulingWindowSchema, type SchedulingWindow } from './getSchedulingWindow';
import type { SchedulingWindowReference, SchedulingWindowSchedule } from './types';

/**
 * The FE-authored request body for creating or updating a scheduling window.
 *
 * **Permissive on purpose** — the server owns the rules (name, timezone and
 * zone_name required; the folder must live in the zone; per-schedule field ranges;
 * no reference cycles) and answers 400. One shape for both writes: the URL
 * carries the identity on PUT, so a caller thinks in terms of "the window's
 * fields" either way.
 *
 * Fields are required-presence with permissive values rather than optional, so a
 * `toSave()` projection that forgets one fails to compile. `folder_id: null`
 * clears the folder and is always sent.
 *
 * `exclusions` is deliberately absent: the backend still merges a legacy
 * `exclusions` array into `references`, but nothing should author one any more.
 */
export interface SaveSchedulingWindow {
  readonly name: string | null;
  readonly description: string | null;
  readonly timezone: string | null;
  readonly zone_name: string | null;
  readonly folder_id: number | null;
  readonly schedules: readonly SchedulingWindowSchedule[];
  readonly references: readonly SchedulingWindowReference[];
}

/**
 * POST `/scheduling_windows`. Returns the created window, serialized through the
 * same `windowDetailsSQL()` as the single read — so the response is validated
 * against the shared `schedulingWindowSchema` rather than a redeclared one.
 * Throws on HTTP error (the caller surfaces the 400/404/409) or on structural
 * validation failure of the response.
 */
export const postSchedulingWindow = async (save: SaveSchedulingWindow): Promise<SchedulingWindow> => {
  const res = await api.post('/scheduling_windows', save);
  const r = runSchema(schedulingWindowSchema, res.data, 'postSchedulingWindow');
  if (!r.ok) {
    throw new Error('postSchedulingWindow: response failed validation');
  }
  return enforce(r.value, r.warnings, ['scheduling_window_id']);
};
